import React, { useRef } from 'react';
import {
  Box,
  IconButton,
  Icons,
  Modal,
  Text,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { HOME_VIEW_ROLE_LABELS, HomeViewRole } from '@app/constants/home';
import { HomeUpcomingAssignment } from '@core/assignments/api/homeUpcomingAssignments';
import InfiniteScrollTrigger from '../../../common/InfiniteScrollTrigger';
import UpcomingAssignmentRow from './UpcomingAssignmentRow';
import UpcomingAssignmentRowSkeleton from './UpcomingAssignmentRowSkeleton';

const MODAL_TITLE_ID = 'upcoming-assignments-modal-title';

const STRINGS = {
  title: 'Upcoming assignments',
  empty: 'No upcoming assignments',
};

type UpcomingAssignmentsModalProps = {
  open: boolean;
  onClose: () => void;
  assignments: HomeUpcomingAssignment[];
  viewRole: HomeViewRole;
  getTermLabel?: (spaceId: string) => string | null;
  onOpenAssignment?: (
    assignment: HomeUpcomingAssignment
  ) => void | Promise<void>;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
};

const UpcomingAssignmentsModal = ({
  open,
  onClose,
  assignments,
  viewRole,
  getTermLabel,
  onOpenAssignment,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: UpcomingAssignmentsModalProps) => {
  const { palette } = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  return (
    <Modal open={open} onClose={onClose} aria-labelledby={MODAL_TITLE_ID}>
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby={MODAL_TITLE_ID}
        sx={{
          width: { xs: 'calc(100vw - 32px)', sm: 616 },
          maxHeight: { xs: 'calc(100vh - 64px)', sm: '80vh' },
          bgcolor: palette.background.paper,
          borderRadius: '12px',
          boxShadow: 24,
          outline: 'none',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={{ xs: 2, sm: 2 }}
          pt={2}
          pb={2}
        >
          <Text
            id={MODAL_TITLE_ID}
            component="h2"
            tabIndex={0}
            sx={{
              fontWeight: 500,
              fontSize: '1rem',
              color: palette.primary.main,
            }}
          >
            {STRINGS.title}
          </Text>
          <IconButton
            onClick={onClose}
            aria-label="Close upcoming assignments dialog"
            sx={{
              color: palette.text.secondary,
              height: '40px',
              width: '40px',
            }}
          >
            <Icons.Close />
          </IconButton>
        </Box>

        <Box
          ref={scrollContainerRef}
          px={{ xs: 2, sm: 2 }}
          pb={2}
          sx={{ overflowY: 'auto', flex: 1 }}
        >
          {assignments.length ? (
            <>
              {assignments.map((assignment, index) => (
                <UpcomingAssignmentRow
                  key={assignment.id}
                  assignment={assignment}
                  viewRole={viewRole}
                  termLabel={getTermLabel?.(assignment.spaceId) ?? null}
                  showDivider={index < assignments.length - 1 || hasMore}
                  onOpen={onOpenAssignment}
                />
              ))}

              {isLoadingMore && (
                <UpcomingAssignmentRowSkeleton showDivider={false} />
              )}

              {onLoadMore && (
                <InfiniteScrollTrigger
                  hasMore={hasMore}
                  isLoadingMore={isLoadingMore}
                  onLoadMore={onLoadMore}
                  rootRef={scrollContainerRef}
                />
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" py={2}>
              {STRINGS.empty} in {HOME_VIEW_ROLE_LABELS[viewRole].toLowerCase()}{' '}
              view.
            </Typography>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default UpcomingAssignmentsModal;
